import React, { ComponentRef, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { AftersalesOrder } from '@1po/1po-bff-fe-spec/generated/aftersales_order/model/AftersalesOrder';
import { Table } from 'antd-v5';
import { ROUTER_ASC_ORDERS } from 'app/AppRouter';
import { isDelegationSessionStatusLoading } from 'domains/delegation/DelegationSession.store';
import { useFetchAscOrders } from 'domains/order/Order.requests';
import { fetchAscOrdersRequestSaga, getAscOrders } from 'domains/order/Order.store';
import AscOrdersFilter from 'pages/MyOrdersPage/AscOrders/Filter/AscOrdersFilter';
import { getAscOrderStatus } from 'pages/MyOrdersPage/AscOrders/Tables/Statuses';
import { getReferenceDisplay, getSupplierNameListDisplay } from 'pages/MyOrdersPage/AscOrders/utils';
import { CenterFlex, Flex, Text, WithTooltip } from 'UI';
import { getSearchData, isLoading, textFormatter } from 'utils';
import { isValidDate } from 'utils/format/formattingService';

const ASC_ORDERS_TABLE_PAGE_SIZE = 50;

// eslint-disable-next-line react/display-name
const AscOrdersTable = forwardRef<ComponentRef<typeof Table>>((_props, ref) => {
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const ascOrdersState = useSelector(getAscOrders);
  const ascOrdersData = getSearchData(ascOrdersState)?.orders ?? [];
  const ascOrdersStatus = ascOrdersState.searchStatus;
  const isDelegationLoading = useSelector(isDelegationSessionStatusLoading);

  useFetchAscOrders();

  const getColumns = () => [
    {
      title: t('asc_order.order_reference', 'Order reference'),
      dataIndex: 'salesOrderNumber',
      key: 'salesOrderNumber',
      render: (salesOrderNumber?: string) => <CenterFlex>{getReferenceDisplay(salesOrderNumber)}</CenterFlex>,
    },
    {
      title: t('asc_order.dms_reference', 'DMS reference'),
      dataIndex: 'salesOrderCustomerReference',
      key: 'salesOrderCustomerReference',
      render: (salesOrderCustomerReference?: string) => (
        <CenterFlex>{getReferenceDisplay(salesOrderCustomerReference)}</CenterFlex>
      ),
    },
    {
      title: t('asc_order.supplier', 'Supplier'),
      dataIndex: 'supplierNames',
      key: 'supplierNames',
      render: (supplierNames?: string[]) => {
        const supplierNamesTooltip =
          supplierNames
            ?.map((name) => name.trim())
            .filter(Boolean)
            .join(', ') || getSupplierNameListDisplay(supplierNames);

        return (
          <Flex maxWidth={300} align={'center'}>
            <WithTooltip title={supplierNamesTooltip}>
              <Text type="base" ellipsis>
                {getSupplierNameListDisplay(supplierNames)}
              </Text>
            </WithTooltip>
          </Flex>
        );
      },
      width: 300,
    },
    {
      title: t('asc_order.date', 'Date'),
      dataIndex: 'orderDate',
      key: 'orderDate',
      render: (orderDate?: string) => {
        if (!orderDate || !isValidDate(orderDate)) {
          return null;
        }
        return <CenterFlex>{textFormatter.formatDateNumeric(new Date(orderDate))}</CenterFlex>;
      },
    },
    {
      title: t('common.price.total_VAT_excluded', 'Total VAT Excluded'),
      dataIndex: 'totalVatExcluded',
      key: 'totalVatExcluded',
      render: (_value: string, row: AftersalesOrder) => {
        const { value, currency } = row;
        return <>{textFormatter.formatCurrency(value ?? 0, currency)}</>;
      },
    },
    {
      title: t('order.order_detail.status', 'Status'),
      dataIndex: 'status',
      key: 'status',
      render: (status?: string) => <CenterFlex>{getAscOrderStatus(status ?? '', t)}</CenterFlex>,
    },
  ];

  const handleRedirectToOrder = (orderReference: string) => {
    history.push(`${ROUTER_ASC_ORDERS}/${orderReference}`);
  };

  const handleChangePage = (page: number) => {
    if (isDelegationLoading) {
      return;
    }
    const lastPage = Math.ceil(ascOrdersData.length / ASC_ORDERS_TABLE_PAGE_SIZE);
    const ascOrdersStateData = getSearchData(ascOrdersState);
    if (page === lastPage && ascOrdersStateData?.hasNextPage) {
      const cursor = ascOrdersStateData?.cursor;
      dispatch(fetchAscOrdersRequestSaga({ cursor }));
    }
  };

  return (
    <Flex direction={'column'} gap={30}>
      <AscOrdersFilter />
      <Table
        bordered
        ref={ref}
        columns={getColumns()}
        dataSource={ascOrdersData}
        rowKey={'salesOrderNumber'}
        loading={isLoading(ascOrdersStatus)}
        pagination={{
          defaultCurrent: 1,
          hideOnSinglePage: true,
          pageSize: 50,
          showSizeChanger: false,
          onChange: handleChangePage,
        }}
        onRow={(record) => {
          return {
            onClick: () => handleRedirectToOrder(record.salesOrderNumber),
            style: { cursor: 'pointer' },
          };
        }}
      />
    </Flex>
  );
});

export default AscOrdersTable;
